import { inject } from '@angular/core';
import { DriversApi } from '@app/api/driver.api';
import { DriverDto, DriverShareRequest } from '@app/models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';

interface DriverState {
  drivers: DriverDto[];
  isLoading: boolean;
  isSubmitting: boolean;
}

const initialState: DriverState = {
  drivers: [],
  isLoading: false,
  isSubmitting: false,
};

export const DriversStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const driversApi = inject(DriversApi);

    const loadDrivers = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { drivers: [], isLoading: true })),
        switchMap(() => driversApi.getDrivers$()),
        tap((drivers) => patchState(store, { drivers, isLoading: false })),
      ),
    );

    const addDriver = rxMethod<{
      request: DriverDto;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          driversApi.createDriver$(request).pipe(
            switchMap((driver) =>
              driversApi.generateDriverPin$(driver.id).pipe(
                tap((createdDriver) => {
                  patchState(store, (state) => ({
                    drivers: [createdDriver, ...state.drivers],
                    isSubmitting: false,
                  }));

                  onSubmit();
                  onSuccess();
                }),
              ),
            ),
          ),
        ),
      ),
    );

    const updateDriver = rxMethod<{
      request: { id: number; driver: DriverDto };
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          driversApi.updateDriver$(request.id, request.driver).pipe(
            tap((updatedDriver) => {
              patchState(store, (state) => ({
                drivers: state.drivers.map((driver) =>
                  driver.id === updatedDriver.id ? updatedDriver : driver,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const shareDriver = rxMethod<{
      request: DriverShareRequest;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ request, onSubmit, onSuccess }) =>
          driversApi.shareDriver$(request).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap((sharedDriver) => {
              patchState(store, (state) => ({
                drivers: state.drivers.map((driver) =>
                  driver.id === sharedDriver.id ? sharedDriver : driver,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const unShareDriver = rxMethod<{
      id: number;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, onSubmit, onSuccess }) =>
          driversApi.unshareDriver$(id).pipe(
            tap((unSharedDriver) => {
              patchState(store, (state) => ({
                drivers: state.drivers.map((driver) =>
                  driver.id === unSharedDriver.id ? unSharedDriver : driver,
                ),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const deleteDriver = rxMethod<{
      id: number;
      onSubmit: () => void;
      onSuccess: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { isSubmitting: true })),
        switchMap(({ id, onSubmit, onSuccess }) =>
          driversApi.removeDriver$(id).pipe(
            catchError(() => {
              patchState(store, { isSubmitting: false });

              return EMPTY;
            }),
            tap(() => {
              patchState(store, (state) => ({
                drivers: state.drivers.filter((driver) => driver.id !== id),
                isSubmitting: false,
              }));

              onSubmit();
              onSuccess();
            }),
          ),
        ),
      ),
    );

    const generatePin = rxMethod<{
      id: number;
      onSuccess: () => void;
    }>(
      pipe(
        switchMap(({ id, onSuccess }) =>
          driversApi.generateDriverPin$(id).pipe(tap(() => onSuccess())),
        ),
      ),
    );

    const getPin = rxMethod<{ id: number; onSuccess: (pin: string) => void }>(
      pipe(
        switchMap(({ id, onSuccess }) =>
          driversApi.getDriverPin$(id).pipe(tap(({ pin }) => onSuccess(pin))),
        ),
      ),
    );

    const clearDrivers = () => patchState(store, initialState);

    return {
      loadDrivers,
      addDriver,
      updateDriver,
      shareDriver,
      unShareDriver,
      deleteDriver,
      generatePin,
      getPin,
      clearDrivers,
    };
  }),
);
